/* eslint-disable prefer-template */
/* eslint-disable @typescript-eslint/no-explicit-any */
import VMask from 'vanilla-masker';
import numeral from 'numeral';
import type { StringifyOptions, ParseOptions } from 'query-string';
import queryString from 'query-string';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import { CnpjFormatter } from '@querodelivery/next-cnpj';
import type { IAddress, IUser } from '@/Interfaces/Data';

const defaultQueryStringStringifyOptions: StringifyOptions = {
  arrayFormat: 'bracket',
  sort: false,
};

const defaultQueryStringParseOptions: ParseOptions = {
  arrayFormat: 'bracket',
  sort: false,
  parseBooleans: true,
};

export const stringifyQueryString = (obj: any) => {
  const keys = Object.keys(obj).filter(key => obj[key] !== '');
  const retorno: any = {};
  keys.forEach(key => (retorno[key] = obj[key]));
  return queryString.stringify(retorno);
};

export const removeNoNumeric = (val: string) => {
  if (val === undefined || val === null) return val;
  return val.toString().replace(/[^\d]+/g, '');
};

export function bytesToHuman(bytes: any) {
  const kb = bytes / 1000;
  if (kb < 1000) return `${kb.toFixed(2)} kb`;
  const mb = kb / 1000;
  return `${mb.toFixed(2)} mb`;
}

export const cpfLabel = (value: string) => {
  return VMask.toPattern(value, '999.999.999-99');
};

export const cnpjLabel = (cnpj: string | number) => {
  if (!cnpj) return '';

  const upperCaseCnpj = String(cnpj).toUpperCase();

  return CnpjFormatter.formatWithMask(upperCaseCnpj);
};

export const smallDateLabel = (value: string) => {
  return VMask.toPattern(value, '99/9999');
};

export const yearDateLabel = (value: string) => {
  return VMask.toPattern(value, '9999');
};

export const cepLabel = (value: string) => {
  return VMask.toPattern(value, '99999-999');
};

export const celularLabel = (value?: string): string => {
  if (!value) return '';
  value = value.replace(/^55/, '');
  value = removeNoNumeric(value);
  if (value.length == 11) return VMask.toPattern(value, '(99) 9 9999-9999');
  return VMask.toPattern(value, '(99) 9999-9999');
};

export const proxyPhoneLabel = (value?: string): string => {
  if (!value) return '';

  const digits = removeNoNumeric(value);

  if (digits.startsWith('0800') && digits.length >= 11) {
    return VMask.toPattern(digits.slice(0, 11), '9999 999 9999');
  }

  return value;
};

export const objectNotNull = (obj: any) => {
  const keys = Object.keys(obj).filter(
    key => obj[key] !== '' && obj[key] !== ' ',
  );
  const retorno: any = {};
  keys.forEach(key => (retorno[key] = obj[key]));
  return retorno;
};

export const objectNotNullV2 = (obj: any) => {
  const keys = Object.keys(obj).filter(
    key =>
      obj[key] !== '' &&
      obj[key] !== ' ' &&
      obj[key] !== null &&
      obj[key] !== undefined,
  );
  const retorno: any = {};
  keys.forEach(key => (retorno[key] = obj[key]));
  return retorno;
};

export const validateEmail = (email: string) =>
  // eslint-disable-next-line no-useless-escape
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    email,
  )
    ? undefined
    : 'E-mail inválido';

export const validateCpf = (cpf: string) => {
  if (cpf === null || cpf === undefined) return false;
  cpf = removeNoNumeric(cpf);
  let numeros: any;
  let digitos: any;
  let soma;
  let i;
  let resultado: any;
  let digitos_iguais;
  digitos_iguais = 1;
  if (cpf.length < 11) return 'CPF inválido';
  for (i = 0; i < cpf.length - 1; i++)
    if (cpf.charAt(i) != cpf.charAt(i + 1)) {
      digitos_iguais = 0;
      break;
    }
  if (!digitos_iguais) {
    numeros = cpf.substring(0, 9);
    digitos = cpf.substring(9);
    soma = 0;
    for (i = 10; i > 1; i--) soma += numeros.charAt(10 - i) * i;
    resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado != digitos.charAt(0)) return 'CPF inválido';
    numeros = cpf.substring(0, 10);
    soma = 0;
    for (i = 11; i > 1; i--) soma += numeros.charAt(11 - i) * i;
    resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado != digitos.charAt(1)) return 'CPF inválido';
    return undefined;
  }
  return undefined;
};

export const validatePhoneBr = (phone: any) => {
  phone = removeNoNumeric(phone);
  return /(^|\()?\s*(\d{2})\s*(\s|\))*(9?\d{4})(\s|-)?(\d{4})($|\n)/.test(phone)
    ? undefined
    : 'Número inválido'; // obrigatorio ter ddd
};

export const stringifyQueryStringArray = (obj: any) => {
  const keys = Object.keys(obj).filter(
    key => obj[key] !== '' && obj[key] !== ' ',
  );
  const retorno: any = {};
  keys.forEach(key => (retorno[key] = obj[key]));
  return queryString.stringify(retorno, { arrayFormat: 'bracket' });
};

export const moneyLabel = (value: string | number) => {
  return numeral(value).format('$ 0,0.00');
};

export const convertMetersToKilometers = (value: number) => {
  return Number((value / 1000).toFixed(2));
};

export const convertKilometersToMeters = (value: number) => {
  return Number(value * 1000);
};

export const moneyInputFormat = (inputText: string) => {
  try {
    if (inputText === null || inputText === undefined || inputText === '')
      return '';
    let tmp: any = parseFloat(removeNoNumeric(inputText) + '');

    tmp = removeNoNumeric((tmp / 100).toFixed(2) + '');

    tmp = tmp.replace(/([0-9]{2})$/g, ',$1');
    if (tmp.length > 6) tmp = tmp.replace(/([0-9]{3}),([0-9]{2}$)/g, '.$1,$2');

    return tmp;
  } catch (ex) {
    return '';
  }
};

export const convertKmInputFormatToFloat = (inputText: string) => {
  if (!inputText) {
    return '';
  }
  return parseFloat(removeNoNumeric(inputText) + '');
};

export const convertMoneyInputFormatToFloat = (inputText: string) => {
  if (!inputText) {
    return '';
  }
  return moneyInputFormat(inputText);
};

export const faresMoneyInputFormat = (inputText: string) => {
  if (!inputText) {
    return '';
  }
  return moneyInputFormat(inputText).replace(',', '.');
};

export const convertStringToFloatWithTwoDecimals = (inputText: string) => {
  if (!inputText) {
    return '0.00';
  }
  const numericValue = parseFloat(removeNoNumeric(inputText) || '0');
  return (numericValue / 100).toFixed(2);
};

export const convertMoneyInputFormatToFloatWithNegative = (
  inputText: string,
) => {
  if (!inputText) {
    return '';
  }
  const isNegative = inputText.startsWith('-');
  const convertedValue = faresMoneyInputFormat(inputText);
  const finalValue = isNegative ? `-${convertedValue}` : convertedValue;
  return finalValue;
};

export const moneyInputFormatToFloat = (inputText: string) => {
  try {
    const valor = inputText.includes('-R$')
      ? inputText.replace('-R$', '') + ''
      : inputText.replace('R$', '') + '';
    const result = parseFloat(
      parseFloat(
        valor
          .split('')
          .filter(char => char != '.')
          .join('')
          .replace(',', '.'),
      ).toFixed(2),
    );
    return result;
  } catch (ex) {
    return 0;
  }
};

export const centsToMoneyLabel = (cents: number) => {
  if (cents == 0) {
    return moneyLabel(0);
  }
  return moneyLabel(cents / 100);
};

export const toType = (obj: any) => {
  return {}.toString.call(obj).match(/\s([a-zA-Z]+)/)
    ? {}.toString
        .call(obj)
        .match(/\s([a-zA-Z]+)/)![1]
        .toLowerCase()
    : null;
};

export const isObjectId = (string: string) => {
  try {
    const checkForHexRegExp = /^[a-f\d]{24}$/i;
    return checkForHexRegExp.test(string);
  } catch (error) {
    return false;
  }
};

export const queryStringToObject = (query: string) => {
  return queryString.parse(query, defaultQueryStringParseOptions);
};

export const stringifyQueryStringToUrl = (query: any, url?: string) => {
  if (url) {
    return queryString.stringifyUrl(
      {
        url,
        query,
      },
      defaultQueryStringStringifyOptions,
    );
  }

  return queryString.stringify(query, defaultQueryStringStringifyOptions);
};

export const setQueryString = (
  array: { key: string; value: any }[],
  arraySeparator = ',',
) => {
  const query: any = {};

  array.forEach(obj => (obj.value ? (query[obj.key] = obj.value) : {}));

  if (Object.keys(query).length > 0) {
    const queryFormatted = queryString.stringify(query, {
      sort: false,
      arrayFormat: 'separator',
      arrayFormatSeparator: arraySeparator,
    });

    const { href } = window.location;

    const origin = href.substring(
      0,
      href.indexOf('?') == -1 ? undefined : href.indexOf('?'),
    );

    const urlFormat = `${origin}?${queryFormatted}`;

    history.replaceState(history.state, '', urlFormat);
  } else {
    const { href } = window.location;

    const origin = href.substring(
      0,
      href.indexOf('?') == -1 ? undefined : href.indexOf('?'),
    );

    history.replaceState(history.state, '', `${origin}`);
  }
};

export const setQueryStringV2 = (object: any) => {
  const queries: any = {};

  Object.keys(object).map(key => {
    const queryValue = object[key];

    if (queryValue && !(isArray(queryValue) && isEmpty(queryValue))) {
      queries[key] = queryValue;
    }
  });

  if (!isEmpty(queries)) {
    const queryFormatted = stringifyQueryStringToUrl(queries);

    const { href } = window.location;

    const origin = href.substring(
      0,
      href.indexOf('?') == -1 ? undefined : href.indexOf('?'),
    );

    const urlFormat = `${origin}?${queryFormatted}`;

    history.replaceState(history.state, '', urlFormat);
  }
};

export const normalizeStatus = (status: string) => {
  let newStatus = String(status);
  newStatus = newStatus.replaceAll('_', ' ');
  newStatus = newStatus.toLowerCase();
  newStatus = newStatus[0].toUpperCase() + newStatus.slice(1);

  return newStatus;
};

export const getAcronym = (word: string) => {
  const acronym = word.slice(0, 3);

  const uppercaseAcronym = acronym.toUpperCase();

  return uppercaseAcronym;
};

export const validationFlag = ({
  flags,
  flagNameValidation,
}: {
  flags: IUser['flags'];
  flagNameValidation: string;
}) => flags && flags.some(f => f.key === flagNameValidation && f.value);

export const removeSpecialCaracteres = (value: string) => {
  return value.replace(/[^0-9a-fA-F]/g, '');
};
export const formatDateToDDMMYYYY = (date?: string) => {
  if (!date) {
    return '';
  }

  if (date.includes('T')) {
    const formattedDate = new Date(date);
    return formattedDate.toLocaleDateString('pt-BR');
  }

  const [year, month, day] = date.split('-').map(Number);
  const parsedDate = new Date(year, month - 1, day);

  return parsedDate.toLocaleDateString('pt-BR');
};

export const isValidLatOrLong = (value: string | number) => {
  return !!(value && !isNaN(Number(value)) && Number(value) !== 0);
};

/**
 * Extrai apenas os valores "sujos" (modificados) de um objeto baseado em um mapa de campos alterados.
 * Esta função é útil para React Hook Form ou sistemas similares onde você precisa identificar
 * quais campos foram modificados pelo usuário e extrair apenas seus valores.
 *
 * @param dirtyFields - Objeto que mapeia quais campos foram modificados.
 *                      - `true` indica que o campo foi alterado
 *                      - objeto aninhado indica campos aninhados alterados
 *                      - array indica elementos de array alterados
 * @param allValues - Objeto contendo todos os valores do formulário
 *
 * @returns Objeto contendo apenas os campos que foram marcados como "dirty" (alterados)
 *
 * @example
 * ```typescript
 * const dirtyFields = {
 *   name: true,
 *   address: {
 *     street: true,
 *     city: false
 *   },
 *   tags: [true, false, true]
 * };
 *
 * const allValues = {
 *   name: "João",
 *   address: {
 *     street: "Rua A",
 *     city: "São Paulo"
 *   },
 *   tags: ["tag1", "tag2", "tag3"]
 * };
 *
 * const result = getDirtyValues(dirtyFields, allValues);
 * // Resultado:
 * // {
 * //   name: "João",
 * //   address: {
 * //     street: "Rua A"
 * //   },
 * //   tags: ["tag1", , "tag3"] // Posições originais preservadas (array esparso)
 * // }
 * ```
 *
 * @remarks
 * - A função é recursiva e lida com objetos aninhados e arrays
 * - Valores undefined, null ou inválidos são tratados graciosamente
 * - Só inclui no resultado final campos que realmente foram alterados
 * - Arrays mantêm as posições originais (array esparso se necessário)
 * - Objetos vazios não são incluídos no resultado final
 *
 * @since 1.0.0
 */
export const getDirtyValues = (dirtyFields: any, allValues: any): any => {
  // Verifica se allValues existe e é um objeto válido
  if (!allValues || typeof allValues !== 'object') {
    return {};
  }

  // Verifica se dirtyFields existe e é um objeto válido
  if (!dirtyFields || typeof dirtyFields !== 'object') {
    return {};
  }

  return Object.keys(dirtyFields).reduce((acc, key) => {
    const dirtyField = dirtyFields[key];
    const value = allValues[key];

    // Campo simples marcado como alterado
    if (dirtyField === true) {
      acc[key] = value;
    }
    // Campo aninhado (objeto) com propriedades alteradas
    else if (
      typeof dirtyField === 'object' &&
      dirtyField !== null &&
      Array.isArray(dirtyField) === false
    ) {
      // Verifica se o valor existe antes de fazer recursão
      if (value !== undefined && value !== null && typeof value === 'object') {
        const nestedResult = getDirtyValues(dirtyField, value);
        // Só adiciona se o resultado não estiver vazio
        if (Object.keys(nestedResult).length > 0) {
          acc[key] = nestedResult;
        }
      }
    }
    // Array com elementos alterados
    else if (Array.isArray(dirtyField)) {
      // Verifica se o valor também é um array
      if (Array.isArray(value)) {
        // Cria array esparso mantendo as posições originais
        const arrayResult: any[] = [];
        let hasValidItems = false;

        dirtyField.forEach((dirtyItem, index) => {
          // Elemento simples alterado
          if (dirtyItem === true) {
            arrayResult[index] = value[index];
            hasValidItems = true;
          }
          // Elemento objeto alterado (recursão)
          else if (typeof dirtyItem === 'object' && value[index]) {
            const nestedResult = getDirtyValues(dirtyItem, value[index]);
            if (Object.keys(nestedResult).length > 0) {
              arrayResult[index] = nestedResult;
              hasValidItems = true;
            }
          }
        });

        // Só adiciona se houver elementos válidos
        if (hasValidItems) {
          acc[key] = arrayResult;
        }
      }
    }
    return acc;
  }, {} as any);
};

export const capitalizeFirstLetter = (value: string) => {
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

export const capitalizeFirstLetterEachWord = (value: string) => {
  return value
    .split(' ')
    .map(word => capitalizeFirstLetter(word))
    .join(' ');
};

export const formatAddress = (address: IAddress) => {
  const number = address.numero || 's/n';
  const city = address.municipioNomeUf
    ? `, ${capitalizeFirstLetterEachWord(address.municipioNomeUf)}`
    : '';
  const logradouro = address.logradouro
    ? `${capitalizeFirstLetterEachWord(address.logradouro)}, `
    : '';
  const bairro = address.bairro
    ? ` - ${capitalizeFirstLetterEachWord(address.bairro)}`
    : '';

  const fullAddress = `${logradouro + number + bairro + city}`;
  return fullAddress;
};

export const formatCurrency = (value: number, currency?: string) => {
  return Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currency || 'BRL',
  }).format(value);
};
